import React, { useRef, useState, useCallback } from 'react';

interface Props {
  onCapture: (dataUrl: string) => void;
  onClose: () => void;
}

export default function CameraCapture({ onCapture, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [error, setError] = useState('');
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  const startCamera = useCallback(async (facing: 'environment' | 'user' = 'environment') => {
    try {
      if (stream) { stream.getTracks().forEach(t => t.stop()); }
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      setStream(s);
      setCameraActive(true);
      setError('');
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.play();
      }
    } catch {
      setError('Não foi possível acessar a câmera. Verifique as permissões.');
    }
  }, [stream]);

  function stopCamera() {
    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
    setCameraActive(false);
  }

  function capture() {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    stopCamera();
    onCapture(dataUrl);
  }

  function flipCamera() {
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacing);
    startCamera(newFacing);
  }

  function handleClose() {
    stopCamera();
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 text-white">
        <button onClick={handleClose} className="text-white text-2xl">✕</button>
        <span className="font-medium">Tirar Foto</span>
        {cameraActive && (
          <button onClick={flipCamera} className="text-white text-xl">🔄</button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center bg-black">
        {cameraActive ? (
          <video ref={videoRef} className="max-h-full max-w-full object-contain" playsInline muted />
        ) : (
          <div className="text-center text-white space-y-4 p-8">
            {error ? (
              <p className="text-red-400">{error}</p>
            ) : (
              <p className="text-gray-400">Câmera não iniciada</p>
            )}
            <button onClick={() => startCamera(facingMode)} className="btn-primary px-6 py-3">
              📷 Iniciar Câmera
            </button>
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      {cameraActive && (
        <div className="p-6 flex justify-center">
          <button
            onClick={capture}
            className="w-16 h-16 rounded-full bg-white border-4 border-gray-300 shadow-lg active:scale-95 transition-transform"
          />
        </div>
      )}
    </div>
  );
}
